import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SvgIcon } from '@/components/SvgIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { notificationsSelectors } from '@/redux/selectors';
import { notificationsActions } from '@/redux/actions';
import { NotificationItem } from '@/redux/models';
import { navigationPages } from '@/config';

import { NotificationRow } from './NotificationRow';
import styles from './NotificationBell.module.sass';

const DROPDOWN_PREVIEW_LIMIT = 10;

interface NotificationListProps {
  // Nullable: a notification need not carry a link, and the handler is responsible
  // for classifying it (internal route vs absolute URL vs unsupported scheme).
  onNavigate: (link: string | null | undefined) => void;
  // Dismisses the panel. Needed because reading an item no longer closes it, so
  // without an explicit control the only way out is clicking off the panel.
  onClose: () => void;
  // Opens the notification in its popup.
  onOpen: (item: NotificationItem) => void;
}

type Filter = 'all' | 'unread';

export const NotificationList: FC<NotificationListProps> = ({
  onNavigate,
  onClose,
  onOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const items = useAppSelector(notificationsSelectors.getItems);
  const { unreadCount, loading } = useAppSelector(
    notificationsSelectors.getNotificationsSlice,
  );

  const [filter, setFilter] = useState<Filter>('all');

  // Activating a row marks it read and opens it in the popup (QA); the popup carries
  // the destination as a button. A link inside the message, or the row's own "Go to"
  // link, supplies its href via `linkHref` and navigates straight there instead.
  const selectHandler = (item: NotificationItem, linkHref?: string) => {
    if (!item.isRead) {
      dispatch(notificationsActions.markRead({ kind: item.kind, id: item.id }));
    }

    if (linkHref) {
      onNavigate(linkHref);
    } else {
      onOpen(item);
    }
  };

  // Filtered on the client: the dropdown is a preview of the newest page, which is
  // what it shows under Unread too. When more unread exist than that page holds, the
  // footer link leads to the rest.
  const visible = (
    filter === 'unread' ? items.filter((item) => !item.isRead) : items
  ).slice(0, DROPDOWN_PREVIEW_LIMIT);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.heading}>
          {t('translation.notifications.title')}
        </span>
        <span className={styles.headerActions}>
          {unreadCount > 0 && (
            <button
              type="button"
              className={styles.markAll}
              onClick={() => dispatch(notificationsActions.markAllRead())}
            >
              {t('translation.notifications.mark_all_read')}
            </button>
          )}
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('translation.close')}
          >
            <SvgIcon name="close" />
          </button>
        </span>
      </div>

      <div className={styles.filters} role="tablist">
        {(['all', 'unread'] as Filter[]).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={filter === value}
            data-testid={`notification-filter-${value}`}
            className={
              filter === value
                ? `${styles.filter} ${styles.filterActive}`
                : styles.filter
            }
            onClick={() => setFilter(value)}
          >
            {value === 'all'
              ? t('translation.all')
              : t('translation.notifications.filter_unread')}
          </button>
        ))}
      </div>

      {/* The only scrolling region — the header above and the footer below stay put.
          Capped at five rows' worth of height in CSS, so a full preview scrolls
          rather than growing the panel past the bottom of a short viewport. */}
      <div className={styles.list}>
        {visible.length === 0 && !loading && (
          <div className={styles.empty}>
            {filter === 'unread'
              ? t('translation.notifications.empty_unread')
              : t('translation.notifications.empty')}
          </div>
        )}

        {/* The dropdown is a capped PREVIEW, not a paginated list. It shares one
            Redux list with the All Notifications page, and its unconditional
            refetch-on-open dispatches a cursor-less fetch that REPLACES items — so if
            it paginated too, opening the bell would collapse a deeply-paged page
            underneath the popover. Depth lives on /notifications; the footer link
            below is the way through. Do not add "load more" here. */}
        {visible.map((item) => (
          <NotificationRow
            key={`${item.kind}-${item.id}`}
            item={item}
            onSelect={selectHandler}
          />
        ))}
      </div>

      <button
        type="button"
        className={styles.viewAll}
        onClick={() => onNavigate(navigationPages.NOTIFICATIONS.path)}
      >
        {t('translation.notifications.view_all')}
      </button>
    </div>
  );
};
